// Medical_Billing_Revised.tsx
import React from 'react';
import { motion , Variants , easeOut} from 'framer-motion';
import { Link } from 'react-router-dom';

// --- Define Animation Variants ---
export const fadeIn: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ⬅️ equivalent of "easeOut"
    },
  },
};

export const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease: [0.42, 0, 1, 1],
    },
  },
};

export const slideInRight: Variants = {
  hidden: { opacity: 0, x: 80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease: [0.42, 0, 1, 1],
    },
  },
};

export const scaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.42, 0, 1, 1],
    },
  },
};

export const staggerChildren: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

export const iconBounce: Variants = {
  initial: { y: 0 },
  hover: {
    y: -8,
    transition: {
      type: "spring" as const,
      stiffness: 400,
      damping: 10,
    },
  },
};

const MedicalBilling: React.FC = () => {
  return (
    <div className="medical-billing-page bg-white text-gray-800 font-sans">

      {/* 1. Hero Section: Clarity & Confidence */}
      <motion.section
        className="relative h-[450px] flex items-center bg-black text-white overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="absolute inset-0 z-0">
          {/* Background Image: Clean, modern medical office with focused professionals */}
          <img
            src="https://s3.wns.com/S3_5/Images/CapabilitiesHeader/HeaderImage/20151/25/Header-Image.png"
            alt="Medical billing professionals ensuring efficiency"
            className="opacity-40 w-full h-full object-cover absolute inset-0"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black opacity-50"></div>
        </div>
        <div className="z-10 max-w-6xl mx-auto px-8 py-16 flex flex-col md:flex-row items-center justify-between">
          <motion.div className="md:w-1/2 text-left md:pr-12 mb-10 md:mb-0" variants={slideInLeft}>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 drop-shadow-lg">
              Hire Medical Billing Virtual Assistant
            </h1>
            <p className="text-xl md:text-1xl mb-8 opacity-90">
             Skilled medical billing virtual assistants help healthcare providers handle correct claims, improve revenue cycle efficiency, streamline procedures, and lower mistakes.
            </p>
            <motion.button
              // className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 text-lg"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
    to="/contact"
    className="inline-block bg-blue-700 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
  >
     Get Your Free Consultation
  </Link>
            
            </motion.button>
          </motion.div>
          <motion.div className="md:w-1/2" variants={slideInRight}>
            {/* Could be an illustration of a perfect revenue cycle or a confident doctor */}
            <img
              src="https://synergyhcls.com/wp-content/uploads/2024/08/The-Medical-Billing-Cycle-in-USA.png" // Example: An SVG illustration representing efficiency/growth
              alt="Revenue cycle optimization illustration"
              className="w-full max-w-md mx-auto"
              loading="lazy"
            />
          </motion.div>
        </div>
      </motion.section>

      ---

      {/* 2. The Core Challenge & Our Solution - Visually Paired */}
      <motion.section
        className="py-20 px-8 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={staggerChildren}
      >
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* Left Side: The Challenge */}
          <motion.div variants={slideInLeft}>
            <h2 className="text-4xl font-bold text-blue-700 mb-6">
              Hire Medical imagining Expert
            </h2>
            <ul className="space-y-4 text-lg text-gray-700">
              <motion.li variants={fadeIn} className="flex items-start">
                <i className="fas fa-exclamation-circle text-red-500 text-2xl mr-4 mt-1"></i>
                <span>To improve diagnostic precision, expedite processes, and provide effective remote healthcare solutions, 
                  hire a medical imaging expert as a virtual assistant</span>
              </motion.li>
            </ul>

             <h2 className="text-4xl font-bold text-blue-700 mb-6">
             Hire medical billing Expert
            </h2>
            <ul className="space-y-4 text-lg text-gray-700">
              <motion.li variants={fadeIn} className="flex items-start">
                <i className="fas fa-exclamation-circle text-red-500 text-2xl mr-4 mt-1"></i>
                <span>To assure correct healthcare billing, optimize reimbursements, and expedite claims processing,
                   hire a medical billing specialist as a virtual assistant.</span>
              </motion.li>
            </ul>
          </motion.div>

          {/* Right Side: Our Solution (Visual representation) */}
          <motion.div variants={slideInRight}>
            <img
              src="https://res.cloudinary.com/upwork-cloud/image/upload/c_scale,w_1000/v1709136410/catalog/1589608331351416832/gxlrdkmmwevdw9xwoh21.webp" // Image: A chaotic billing scenario transforming into an organized one (split image or creative composition)
              alt="Challenges in medical billing and our solutions"
              className="rounded-xl shadow-2xl w-full h-auto"
              loading="lazy"
            />
          </motion.div>
        </div>
      </motion.section>

      ---

      {/* 3. Our Value Proposition / Core Benefits - Icon Grid with Descriptions */}
      <motion.section
        className="py-20 px-8 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={staggerChildren}
      >
        <h2 className="text-4xl font-bold text-center text-blue-700 mb-14">
          Experience the Difference: Key Advantages of Partnering With Us
        </h2>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Benefit Card 1: Maximized Revenue */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-blue-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <motion.div
              className="text-6xl text-blue-600 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-chart-line"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Maximized Revenue</h3>
            <p className="text-gray-700">We optimize every claim, ensuring higher collection rates and accelerated cash flow.</p>
          </motion.div>

          {/* Benefit Card 2: Unwavering Compliance */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-green-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <motion.div
              className="text-6xl text-green-600 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-shield-alt"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Unwavering Compliance</h3>
            <p className="text-gray-700">Stay ahead of ever-changing regulations (HIPAA, coding) with our certified experts.</p>
          </motion.div>

          {/* Benefit Card 3: Enhanced Efficiency */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-purple-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <motion.div
              className="text-6xl text-purple-600 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-rocket"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Enhanced Efficiency</h3>
            <p className="text-gray-700">Reduce administrative burden, allowing your staff to prioritize patient care.</p>
          </motion.div>

          {/* Benefit Card 4: Complete Transparency */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-orange-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <motion.div
              className="text-6xl text-orange-600 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-eye"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Complete Transparency</h3>
            <p className="text-gray-700">Access real-time, actionable reports and insights into your billing performance.</p>
          </motion.div>

          {/* Benefit Card 5: Dedicated Partnership */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-teal-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <motion.div
              className="text-6xl text-teal-600 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-handshake"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Dedicated Partnership</h3>
            <p className="text-gray-700">We act as an extension of your team, providing personalized support and expertise.</p>
          </motion.div>

          {/* Benefit Card 6: Error Reduction */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-red-50 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <motion.div
              className="text-6xl text-red-600 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-check-circle"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Near-Zero Error Rate</h3>
            <p className="text-gray-700">Meticulous claim scrubbing ensures accurate submissions and fewer rejections.</p>
          </motion.div>
        </div>
      </motion.section>

      ---

      {/* 4. Our Process - Clear, Step-by-Step with Visuals */}
      <motion.section
        className="py-20 px-8 bg-blue-700 text-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={staggerChildren}
      >
        <h2 className="text-4xl font-bold text-center mb-16">
          Our Seamless Path to Your Revenue Success
        </h2>
        <div className="max-w-6xl mx-auto relative">
          {/* Horizontal Timeline / Process flow */}
          <div className="hidden lg:flex justify-between items-center absolute top-1/2 left-0 right-0 -translate-y-1/2">
            <div className="w-full h-1 bg-teal-400"></div>
            <div className="absolute top-1/2 left-0 w-4 h-4 rounded-full bg-white border-2 border-teal-400 -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-1/2 right-0 w-4 h-4 rounded-full bg-white border-2 border-teal-400 translate-x-1/2 -translate-y-1/2"></div>
          </div>

          <div className="grid lg:grid-cols-4 gap-12 relative z-10">
            {/* Step 1 */}
            <motion.div
              className="bg-white text-gray-900 p-8 rounded-lg shadow-xl text-center flex flex-col items-center"
              variants={scaleUp}
            >
              <div className="text-6xl text-blue-600 mb-4">
                <i className="fas fa-user-check"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">1. Onboarding & Setup</h3>
              <p className="text-gray-700">Smooth integration with your EMR/EHR system and practice details.</p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              className="bg-white text-gray-900 p-8 rounded-lg shadow-xl text-center flex flex-col items-center"
              variants={scaleUp}
              transition={{ delay: 0.1 }}
            >
              <div className="text-6xl text-green-600 mb-4">
                <i className="fas fa-file-invoice-dollar"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">2. Claim Processing</h3>
              <p className="text-gray-700">Meticulous scrubbing, coding, and submission of all claims.</p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              className="bg-white text-gray-900 p-8 rounded-lg shadow-xl text-center flex flex-col items-center"
              variants={scaleUp}
              transition={{ delay: 0.2 }}
            >
              <div className="text-6xl text-orange-600 mb-4">
                <i className="fas fa-hand-holding-medical"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">3. Follow-up & Appeals</h3>
              <p className="text-gray-700">Proactive management of denials and underpayments for maximum returns.</p>
            </motion.div>

            {/* Step 4 */}
            <motion.div
              className="bg-white text-gray-900 p-8 rounded-lg shadow-xl text-center flex flex-col items-center"
              variants={scaleUp}
              transition={{ delay: 0.3 }}
            >
              <div className="text-6xl text-teal-600 mb-4">
                <i className="fas fa-chart-pie"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">4. Reporting & Analysis</h3>
              <p className="text-gray-700">Receive comprehensive, actionable insights into your financial performance.</p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      ---

      {/* 5. Trust & Authority Section (HIPAA & Certifications) */}
      <motion.section
        className="py-20 px-8 bg-gray-50 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={fadeIn}
      >
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-6xl text-blue-600 mb-6"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <i className="fas fa-lock"></i>
          </motion.div>
          <h2 className="text-4xl font-bold text-blue-700 mb-6">
            Your Trust. Our Commitment. Uncompromising Security.
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            We operate with the highest standards of HIPAA compliance and data security, ensuring your patient information and financial data are always protected. Our team is **certified and continuously trained** on the latest industry regulations and coding practices.
          </p>
          <div className="flex justify-center space-x-8 items-center mt-10">
            <motion.img
              src="https://cdn.prod.website-files.com/5ef17a27d496ca6ed6ac71aa/651c4130c70a6abcd2e58f31_hipaa_blue.png" // Example: HIPAA compliant logo
              alt="HIPAA Compliant"
              className="h-20 opacity-80 hover:opacity-100 transition-opacity duration-300"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: 0.2 }}
              loading="lazy"
            />
            <motion.img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTOaQLEPPOXekOqEJ9DHkpytfDegMOIcpcq4ahWzdVj3Ls6jew2ieigeOKF-9wFGiGTAzM&usqp=CAU" // Example: Certified Medical Biller logo
              alt="Certified Billing Experts"
              className="h-20 opacity-80 hover:opacity-100 transition-opacity duration-300"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ delay: 0.4 }}
              loading="lazy"
            />
          </div>
        </div>
      </motion.section>

      ---

      {/* 6. Testimonials / Success Stories - Carousel or Grid */}
      <motion.section
        className="py-20 px-8 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={staggerChildren}
      >
        <h2 className="text-4xl font-bold text-center text-blue-700 mb-14">
          Driving Real Results for Practices Like Yours
        </h2>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Testimonial Card 1 */}
          <motion.div
            className="bg-gray-50 p-8 rounded-lg shadow-lg flex flex-col items-start text-left hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <div className="flex items-center mb-6">
              <img
                src="https://static.vecteezy.com/system/resources/thumbnails/028/287/555/small/an-indian-young-female-doctor-isolated-on-green-ai-generated-photo.jpg" // Client Avatar
                alt="Dr. Li"
                className="w-20 h-20 rounded-full mr-6 border-4 border-teal-400 object-cover"
                loading="lazy"
              />
              <div>
                <p className="text-xl font-semibold text-gray-900">Dr. Anya Li</p>
                <p className="text-md text-gray-600">Pediatric Specialist, Horizon Health</p>
              </div>
            </div>
            <p className="text-lg italic text-gray-700 leading-relaxed mb-4">
              &quot;Our revenue cycle was a mess. [ Nadezhda Venture Pvt. Ltd.] brought unparalleled clarity and efficiency. We've seen a **20% increase in collections** and our staff is happier.&quot;
            </p>
            <div className="text-yellow-500 text-lg">
              <i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star-half-alt"></i>
            </div>
          </motion.div>

          {/* Testimonial Card 2 */}
          <motion.div
            className="bg-gray-50 p-8 rounded-lg shadow-lg flex flex-col items-start text-left hover:shadow-xl transition-shadow duration-300"
            variants={scaleUp}
          >
            <div className="flex items-center mb-6">
              <img
                src="https://static.vecteezy.com/system/resources/thumbnails/026/375/249/small/ai-generative-portrait-of-confident-male-doctor-in-white-coat-and-stethoscope-standing-with-arms-crossed-and-looking-at-camera-photo.jpg" // Client Avatar
                alt="Clinic Manager"
                className="w-20 h-20 rounded-full mr-6 border-4 border-blue-400 object-cover"
                loading="lazy"
            />
              <div>
                <p className="text-xl font-semibold text-gray-900">Michael Chen</p>
                <p className="text-md text-gray-600">Clinic Administrator, Metro Medical Group</p>
              </div>
            </div>
            <p className="text-lg italic text-gray-700 leading-relaxed mb-4">
              &quot;The transition was seamless. Their proactive denial management saved us countless hours and significantly improved our cash flow. Truly a game-changer.&quot;
            </p>
            <div className="text-yellow-500 text-lg">
              <i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i><i className="fas fa-star"></i>
            </div>
          </motion.div>
        </div>
      </motion.section>


    </div>
  );
};

export default MedicalBilling;